import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fmtBytes, fmtDate, fileName, folderOf } from "@/lib/api";

export default function Documents() {
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("all");
  const { data: docs = [], isLoading } = useQuery({ queryKey: ["documents"], queryFn: () => api.get("/admin/documents").then((r) => r.data) });

  const folders = useMemo(() => [...new Set(docs.map((d) => folderOf(d.key)))].sort(), [docs]);
  const rows = docs.filter((d) => (folder === "all" || folderOf(d.key) === folder) && d.key.toLowerCase().includes(q.toLowerCase()));
  const totalChunks = rows.reduce((s, d) => s + d.chunks, 0);

  return (
    <div className="space-y-8">
      <div className="rise">
        <p className="font-mono text-xs uppercase tracking-wider text-indigo-400">Knowledge index</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">S3 Documents</h1>
        <p className="text-slate-400 mt-2 text-sm sm:text-base" data-testid="documents-summary">{rows.length} files · {totalChunks.toLocaleString()} chunks</p>
      </div>

      <div className="bg-[#0F172A] border border-slate-800 rounded-xl overflow-hidden rise" style={{ animationDelay: "100ms" }}>
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-800">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="documents-search-input" placeholder="Search file name…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 bg-[#080C14] border-slate-700" />
          </div>
          <Select value={folder} onValueChange={setFolder}>
            <SelectTrigger data-testid="documents-folder-filter" className="sm:w-80 h-9 bg-[#080C14] border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All folders</SelectItem>
              {folders.map((f) => <SelectItem key={f} value={f}>{f.split("/").slice(-1)[0] || f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="documents-table">
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                {["File", "Folder", "Type", "Chunks", "Size", "Last indexed"].map((h) => (
                  <TableHead key={h} className="font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.key} data-testid="documents-row" className="border-slate-800 hover:bg-slate-800/40 transition-colors">
                  <TableCell className="text-sm text-slate-100 max-w-[320px] break-all">{fileName(d.key)}</TableCell>
                  <TableCell className="font-mono text-[11px] text-slate-500 max-w-[240px] break-all">{folderOf(d.key)}</TableCell>
                  <TableCell><span className="font-mono text-[11px] uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">{d.key.split(".").pop()}</span></TableCell>
                  <TableCell className="font-mono text-sm">{d.chunks}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-400 whitespace-nowrap">{fmtBytes(d.size)}</TableCell>
                  <TableCell className="text-sm text-slate-400 whitespace-nowrap">{fmtDate(d.indexed_at)}</TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={6} data-testid="documents-empty" className="text-center py-12 text-slate-500 text-sm">{isLoading ? "Loading…" : "No documents match."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
